import { createAsyncThunk } from '@reduxjs/toolkit';
import { JackpotsService } from '../../services/jackpots.service';
import { NgProgressiveJackpotsService } from '../../services/ng-progressive-jackpots.service';
import { verticalActionTypesEnum, verticalTypesEnum } from '../../enums/vertical-types.enum';
import { useAsgActions } from '../../hooks/use-asg-actions.hook';

//region PariPlay Jackpots
export const getJackpots = createAsyncThunk(
    'jackpots/fetchJackpots',
    async (requestParams, { getState, dispatch }) => {
        try {
            const { fillJackpotsVertical } = useAsgActions();
            const {
                app: {
                    config: { templateDirectoryUri },
                    verticals,
                },
                player: {
                    isLoggedIn,
                },
            } = getState();
            const { data } = await JackpotsService.getJackpots(requestParams);

            const jackpotsFiltered = await JackpotsService.processJackpotsApiResponse({
                verticals,
                verticalTypesEnum,
                verticalActionTypesEnum,
                games: AsgFW.getGamesList().instant,
                jackpots: data.jackpots,
                jackpotsByOrder: data.byOrder,
                jackpotsBanners: data.banners,
                jackpotsCategories: data.categories,
                themePublicPath: templateDirectoryUri,
            });

            if (isStateChangedDuringFetching(getState, isLoggedIn, verticals)) {
                throw new Error('Important changes happened during JackPots fetching.');
            }

            const jackpotsVertical = jackpotsFiltered.verticals
                .find(vertical => vertical.type === verticalTypesEnum.JACKPOT);

            dispatch(fillJackpotsVertical(jackpotsVertical));

            return {
                ...jackpotsFiltered,
                winners: data.winners,
                maturityThresholds: data.maturityThresholds,
            };
        } catch {
            return null;
        }
    }
);

/**
 * Checks is SSO and/or verticals were changed during JP fetching.
 *
 * Allows to prevent overriding just got verticals with old ones.
 * @param {function} getState
 * @param {boolean} initialSso
 * @param {array} initialVerticals
 * @return {boolean}
 */
const isStateChangedDuringFetching = (getState, initialSso, initialVerticals) => {
    const {
        app: {
            verticals: currentVerticals,
        },
        player: {
            isLoggedIn: finalSso,
        },
    } = getState();
    const initialVerticalIds = initialVerticals.map(v => v.id);
    const currentVerticalIds = currentVerticals.map(v => v.id);
    let verticalsIdentical = true;
    for (let i = 0; i < currentVerticalIds.length; i++) {
        if (initialVerticalIds[i] !== currentVerticalIds[i]) {
            verticalsIdentical = false;
        }
    }

    return initialSso !== finalSso || !verticalsIdentical;
};
//endregion

//region NG Progressive Jackpots
export const getNgProgressiveJackpots = createAsyncThunk(
    'jackpots/fetchNgProgressiveJackpots',
    async (requestParams) => {
        try {
            const currencyCode = requestParams.currencyCode;
            const pjps = await NgProgressiveJackpotsService.getProgressiveJackpots({ currencyCode });
            const amountsById = {};

            pjps['ProgressiveJackpots'].forEach(pjp => {
                const pjpId = pjp.GlobalIdentifier;
                const pjpCurrency = pjp.CurrencyCode;
                const pjpAmount = prepareNgProgressiveJackpotAmount(pjp?.PJPBalanceCurrent);

                if (!pjpId || !pjpCurrency || !pjpAmount) {
                    return;
                }

                if (!Object.hasOwn(amountsById, pjpCurrency)) {
                    amountsById[pjpCurrency] = {};
                }

                amountsById[pjpCurrency][pjpId] = pjpAmount;
            });

            return {
                amountsById,
            };
        } catch {
            return null;
        }
    },
    {
        condition: (props, { getState }) => {  
            const { jackpots: { ngProgressiveJackpots: { participantGamesExist } } } = getState();

            // Prevent calls to API if no participant Games exist
            return Boolean(participantGamesExist);
        }
    }
);

// Converting PJP's amount from a string '12,345.67' to an integer 12345
const prepareNgProgressiveJackpotAmount = originAmount => {
    let convertedAmount = null;
    const amountAsString = originAmount.toString();

    // RegExp: tests if string contains only numbers, commas, and/or dots
    if (/^[\d,.]+$/.test(amountAsString)) {
        convertedAmount = amountAsString.replace(/,/g, '').split('.')[0];
        convertedAmount = parseInt(convertedAmount, 10);
    }

    return convertedAmount;
};
//endregion
